import { apiClient } from './client';
import { getNumberLocale } from '../lib/base-i18n';
import type { PremiumMinutesDto } from './attendance';

/**
 * 임금 프로필 · 연장 정산 (근태 E2, 2026-08-24).
 *
 * 서버 SSOT: `apps/chat-server/src/modules/hr/wage-profile.routes.ts`
 * 설계: `docs/attendance-round2-sample.html` 제안 7 · 화면 Q.
 *
 * ★★ 여기 오는 금액은 전부 **예상**이다. 확정은 급여 명세를 만들 때 정해진다 —
 *    화면이 "예상" 을 지우면 두 숫자가 생긴다.
 * ★★ `amount === null` 은 **0 원이 아니라 "임금 정보 없음"** 이다. 0 으로 그리면
 *    "수당이 없다" 로 읽히는데, 그것은 사실이 아니라 우리가 모른다는 뜻이다.
 */

export const WAGE_TYPES = ['MONTHLY', 'ANNUAL', 'HOURLY', 'INCLUSIVE'] as const;
export type WageType = (typeof WAGE_TYPES)[number];

export const WAGE_TYPE_LABEL: Record<WageType, string> = {
  MONTHLY: '월급제', ANNUAL: '연봉제', HOURLY: '시급제', INCLUSIVE: '포괄임금',
};

/** 통상임금 시급의 나눗수 기본값 — 209시간. */
export const DEFAULT_MONTHLY_STANDARD_MINUTES = 209 * 60;

export interface WageProfileDto {
  id: string;
  userId: string;
  wageType: string;
  wageTypeLabel: string;
  /** 월 통상임금(월급·포괄) 또는 연봉(연봉제) — 원. */
  baseAmount: number | null;
  /** 약정 시급(시급제) — 원. */
  hourlyRate: number | null;
  /** 포괄임금 약정 연장시간(분). null 이면 판정하지 않는다. */
  fixedOtMinutes: number | null;
  monthlyStandardMinutes: number;
  effectiveFrom: string;
  memo: string | null;
  /** 통상임금 시급 — 저장값이 아니라 서버가 낸 **파생**이다. */
  hourlyWage: number | null;
  updatedAt: string;
}

export type EstimateReason = 'OK' | 'NO_PROFILE' | 'NO_BASE' | 'NO_FIXED_HOURS' | 'WITHIN_FIXED';

export interface WageEstimateDto {
  /** **null 은 0 이 아니다** — 계산할 재료가 없다는 뜻이다. */
  amount: number | null;
  reason: EstimateReason;
  countedMinutes: number;
  hourlyWage: number | null;
  multiplierNote: string;
}

export interface SettlementRowDto {
  userId: string;
  name: string;
  employeeNumber: string | null;
  wageType: string | null;
  wageTypeLabel: string | null;
  premium: PremiumMinutesDto;
  /**
   * 휴일대체(§57)로 **휴가로 갈음된** 몫과, 남아서 **수당이 되는** 몫 (2026-09-06 · 판 B).
   *
   * ★★★ `premium` 은 「그 달에 얼마나 일했나」라 갈음과 무관하게 그대로다. 금액은
   *    `payable` 로만 낸다 — 휴가로 주고 수당도 주면 이중이다. 옛 서버는 이 둘을 안 준다.
   */
  substituted?: PremiumMinutesDto;
  payable?: PremiumMinutesDto;
  estimate: WageEstimateDto;
}

export interface SettlementDto {
  year: number;
  month: number;
  rows: SettlementRowDto[];
  total: {
    premium: PremiumMinutesDto;
    /** 계산할 수 있었던 사람들의 합 — **전원의 합이 아니다**. */
    amount: number;
    /** 임금 정보가 없어 금액을 못 낸 사람 수. */
    unknownWage: number;
    people: number;
    /** 판 B — 갈음된 합과 수당이 되는 합. 옛 서버는 안 준다. */
    substituted?: PremiumMinutesDto;
    payable?: PremiumMinutesDto;
    /** 갈음이 한 줄이라도 있는 사람 수 — **0 이면 화면이 그 열을 아예 안 그린다.** */
    substitutedPeople?: number;
  };
  basis: {
    nightWindow: string; holidayDates: number; weeklyRestCounted: boolean;
    /** 금액이 무엇에서 나왔나 — `payable` 이면 갈음된 몫이 빠진 값이다(판 B). */
    amountFrom?: 'payable';
  };
}

export interface WageProfileInput {
  wageType: WageType;
  baseAmount?: number | null;
  hourlyRate?: number | null;
  fixedOtMinutes?: number | null;
  monthlyStandardMinutes?: number;
  effectiveFrom: string;
  memo?: string | null;
}

/** 그 달 연장 정산(화면 Q). 연장이 있는 사람만 온다. */
export async function getOvertimeSettlement(year?: number, month?: number): Promise<SettlementDto> {
  const { data } = await apiClient.get<SettlementDto>('/hr/overtime-settlement', {
    params: year && month ? { year, month } : undefined,
  });
  return { ...data, rows: Array.isArray(data?.rows) ? data.rows : [] };
}

/** 회사 사람들의 현재 유효 임금 프로필. 없는 사람은 오지 않는다. */
export async function listWageProfiles(): Promise<WageProfileDto[]> {
  const { data } = await apiClient.get<{ profiles: WageProfileDto[] }>('/hr/wage-profiles');
  return Array.isArray(data?.profiles) ? data.profiles : [];
}

/** 한 사람의 이력 — 임금이 바뀌면 행이 쌓인다(고치지 않고 새로 넣는다). */
export async function getWageProfileHistory(userId: string): Promise<WageProfileDto[]> {
  const { data } = await apiClient.get<{ history: WageProfileDto[] }>(`/hr/wage-profiles/${userId}`);
  return Array.isArray(data?.history) ? data.history : [];
}

export async function saveWageProfile(userId: string, input: WageProfileInput): Promise<WageProfileDto> {
  const { data } = await apiClient.put<{ profile: WageProfileDto }>(`/hr/wage-profiles/${userId}`, input);
  return data.profile;
}

/** 잘못 넣은 행 삭제. **임금이 바뀐 것은 삭제가 아니라 새 행**이다. */
export async function deleteWageProfileRow(id: string): Promise<void> {
  await apiClient.delete(`/hr/wage-profiles/row/${id}`);
}

/** `1,234,000원`. null 이면 호출부가 "임금 정보 없음" 을 쓴다 — 여기서 0 을 지어내지 않는다. */
export function formatWon(amount: number): string {
  // ★ 로케일을 박아 넣지 않는다 — 화면 언어를 따른다(i18n 게이트가 이 규칙을 지킨다).
  return `${amount.toLocaleString(getNumberLocale())}원`;
}
